import type { BannerThemeVars } from "../types";

const STYLE_ID = "swift-banner-styles";

const DEFAULTS: Required<BannerThemeVars> = {
  primary: "#2563eb",
  bg: "#0b1220",
  text: "#e5e7eb",
  fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial",
  zIndex: 2147483000,
};

export function mergeTheme(
  overrides?: BannerThemeVars,
): Required<BannerThemeVars> {
  return { ...DEFAULTS, ...overrides };
}

export function buildCss(theme: Required<BannerThemeVars>): string {
  return `
    .sw-root { position: fixed; inset: 0; display: grid; place-items: center;
      --sw-primary: ${theme.primary};
      --sw-bg: ${theme.bg};
      --sw-text: ${theme.text};
      --sw-font: ${theme.fontFamily};
      z-index: ${theme.zIndex};
    }
    .sw-root[hidden] { display: none !important; }
    .sw-overlay { position: absolute; inset: 0; background: color-mix(in oklab, black 60%, transparent); opacity: 0; }
    .sw-dialog { position: relative; width: min(92vw, 720px); max-height: 86vh; overflow: auto; border-radius: 1rem;
      background: var(--sw-bg); color: var(--sw-text); box-shadow: 0 10px 30px rgba(0,0,0,.35);
      transform: translateY(8px) scale(.98); opacity: 0; font-family: var(--sw-font); }
    .sw-header { padding: 1rem 3rem 0 1rem; }
    .sw-title { font-size: 1.125rem; line-height: 1.5rem; font-weight: 700; margin: 0; }
    .sw-close { position: absolute; top: 4px; right: 4px; background: transparent; border: 0; cursor: pointer; padding: 8px; border-radius: .5rem; color: var(--sw-text); z-index: 1; }
    .sw-close:hover { background: rgba(255,255,255,.06); }
    .sw-close:focus { outline: none; box-shadow: 0 0 0 2px var(--sw-primary); }
    .sw-body { display: grid; gap: .75rem; }
    .sw-text { margin: 0; font-size: .975rem; opacity: .95; }
    .sw-image { width: 100%; height: auto; border-radius: .75rem; transition: transform 0.2s ease, filter 0.2s ease; display: block; }
    @media (max-width: 768px) {
      .sw-image { max-height: 85vh; object-fit: contain; }
    }
    .sw-image-link { display: block; text-decoration: none; cursor: pointer; overflow: hidden; border-radius: .75rem; }
    .sw-image-link:hover .sw-image { transform: scale(1.02); filter: brightness(1.05); }
    .sw-image-link:focus { outline: none; border-radius: .75rem; }

    @keyframes sw-overlay-in { from { opacity: 0 } to { opacity: 1 } }
    @keyframes sw-overlay-out { from { opacity: 1 } to { opacity: 0 } }
    @keyframes sw-dialog-in { from { opacity: 0; transform: translateY(16px) scale(.96) } to { opacity: 1; transform: translateY(0) scale(1) } }
    @keyframes sw-dialog-out { from { opacity: 1; transform: translateY(0) scale(1) } to { opacity: 0; transform: translateY(8px) scale(.98) } }

    .sw-root[data-state="enter"] .sw-overlay { animation: sw-overlay-in 240ms ease forwards; }
    .sw-root[data-state="enter"] .sw-dialog { animation: sw-dialog-in 240ms cubic-bezier(.2,.7,.2,1) forwards; }
    .sw-root[data-state="exit"] .sw-overlay { animation: sw-overlay-out 200ms ease forwards; }
    .sw-root[data-state="exit"] .sw-dialog { animation: sw-dialog-out 200ms ease forwards; }

    @media (prefers-reduced-motion: reduce) {
      .sw-root[data-state="enter"] .sw-overlay,
      .sw-root[data-state="enter"] .sw-dialog,
      .sw-root[data-state="exit"] .sw-overlay,
      .sw-root[data-state="exit"] .sw-dialog {
        animation: none !important; opacity: 1; transform: none;
      }
    }
  `;
}

export function injectStyles(theme: Required<BannerThemeVars>): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = buildCss(theme);
  document.head.appendChild(style);
}

export function removeStyles(): void {
  document.getElementById(STYLE_ID)?.remove();
}
